import { pageUrl } from '@constants/url';
import {
  Ban,
  BarChart3,
  Bell,
  Boxes,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CirclePlus,
  ClipboardCheck,
  CreditCard,
  Crown,
  Database,
  DollarSign,
  FileArchive,
  FileInput,
  Forward,
  Gamepad2,
  GraduationCap,
  History,
  Landmark,
  List,
  Mail,
  Newspaper,
  Power,
  Radar,
  Reply,
  Scissors,
  Search,
  Settings,
  Shield,
  Ship,
  Stamp,
  Table2,
  Truck,
  UsersRound,
  Warehouse,
  X,
  Building2,
  Factory,
  Package,
} from 'lucide-react';
import LinksGroup from '@components/sidebar/links-group';
import { Tooltip, Menu, Popover } from '@mantine/core';
import useAuth from '@hooks/use-auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useState, useEffect, useMemo, useRef } from 'react';
import { impersonationUtils } from '@utils/impersonation';
import { useStaffAccess } from '@hooks/useStaffAccess';
import ImpersonationSelect from '@components/impersonation/ImpersonationSelect';
import LanguageSwitcher from '@components/language/LanguageSwitcher';
import NotificationBell from '@components/notifications/NotificationBell';
import TrialBadge from '@components/subscription/TrialBadge';
import AppUpdateHeaderButton from '@components/app-update/AppUpdateHeaderButton';
import { isProfessionalModeEnabled } from '@utils/professionalMode';
import { useCompanyFeatures } from '@contexts/CompanyFeaturesContext';
import { usePermissions } from '@contexts/PermissionsContext';
import { BILLING_ENABLED } from '@constants/features';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import FeatureLockedModal from '@components/feature-lock/FeatureLockedModal';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useHaptic, haptic, HAPTIC } from '@utils/haptic';
import { LIGHT_THEME_MODES, type ThemeMode } from '@constants/colors';
import { sidebarAnchor, TourHelpButton } from '@/features/onboarding';
import { useUnreadPatchLogCount } from '@hooks/patch-logs/use-patch-logs';
import { isDemoSession } from '@utils/demo-account';

/** Themes whose sidebar surface is light — active pills must use dark text. */
const LIGHT_SIDEBAR_MODES: ThemeMode[] = LIGHT_THEME_MODES;

/** Align with Mantine AppShell `md` (~992px). */
const MOBILE_BREAKPOINT = 992;

const iconClass = 'shrink-0';
const navIcon = (Icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>) => (
  <Icon className={iconClass} size={16} strokeWidth={1.5} />
);

interface SidebarV2Props {
  isOpen: boolean;
  isMinimized: boolean;
  toggleDrawer: () => void;
  toggleMinimize: () => void;
  isVisible?: boolean;
}

// Emails that are view-only for Kepabeanan (no edit in both BC 2.0 and BC 3.0)
const KEPABEANAN_VIEW_ONLY_EMAILS = [
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'panji@gateway-id.com',
];

// Emails that are view-only for Manifest
const MANIFEST_VIEW_ONLY_EMAILS = [
  'panji@gateway-id.com',
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'christina.vonny@molgroup.com',
  'ari.wibowo@molgroup.com',
  'femy.muthia@molgroup.com',
  'reza.haqqa@molgroup.com',
  'ratri.puji@molgroup.com',
  'mlg-id.airexport01@molgroup.com',
];

const menus = [
  {
    label: 'Mgmt Dashboard',
    i18nKey: 'menu.mgmtDashboard',
    icon: navIcon(Crown),
    link: pageUrl.managementDashboard,
    badge: 'BOD',
  },
  {
    label: 'Manifest',
    i18nKey: 'menu.manifest',
    icon: navIcon(Ship),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: '/manifest/dashboard',
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/manifest/product',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: '/manifest/draft',
        isMinimized: false,
      },
      {
        label: 'Doc Manifes',
        i18nKey: 'menu.docManifes',
        icon: navIcon(FileInput),
        link: '/manifest/doc-pabean',
        isMinimized: false,
      },
      {
        label: 'The Guardian',
        i18nKey: 'menu.theGuardian',
        icon: navIcon(Bell),
        link: '/manifest/guardian',
        isMinimized: false,
      },
      {
        label: 'Radar',
        i18nKey: 'menu.radar',
        icon: navIcon(Radar),
        link: '/manifest/guardian/radar',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Kepabeanan',
    i18nKey: 'menu.kepabeanan',
    icon: navIcon(FileInput),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: pageUrl.dashboard,
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/buat-aju',
        isMinimized: false,
      },
      {
        label: 'Doc Pabean',
        i18nKey: 'menu.docPabean',
        icon: navIcon(FileInput),
        link: pageUrl.docPabean,
        isMinimized: false,
      },
      {
        label: 'Monitoring Blokir',
        i18nKey: 'menu.monitoringBlokir',
        icon: navIcon(Ban),
        link: '/monitoring-blokir',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: pageUrl.draftDocPabean,
        isMinimized: false,
      },
      {
        label: 'Respon',
        i18nKey: 'menu.respon',
        icon: navIcon(Reply),
        link: '/respon',
        isMinimized: false,
      },
      {
        label: 'Database Barang',
        i18nKey: 'menu.databaseBarang',
        icon: navIcon(Database),
        link: '/database-barang',
        isMinimized: false,
      },
      {
        label: 'PPFTZ',
        i18nKey: 'menu.ppftz',
        icon: navIcon(Warehouse),
        link: pageUrl.ppftz.list,
        isMinimized: false,
        badge: 'New',
      },
    ],
  },
  {
    label: 'Kuota Impor / Ekspor',
    i18nKey: 'menu.kuotaImporEkspor',
    icon: navIcon(Table2),
    link: '#',
    subLinks: [
      {
        label: 'Shipment Quota',
        i18nKey: 'menu.shipmentQuota',
        icon: navIcon(Table2),
        link: '/shipment-quota',
        isMinimized: false,
      },
      {
        label: 'Harga Referensi',
        i18nKey: 'menu.hargaReferensi',
        icon: navIcon(DollarSign),
        link: '/harga-referensi',
        isMinimized: false,
      },
      {
        label: 'Tarif TRQ',
        i18nKey: 'menu.tarifTrq',
        icon: navIcon(DollarSign),
        link: '/tarif-trq',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Cukai',
    i18nKey: 'menu.cukai',
    icon: navIcon(Stamp),
    link: '#',
    badge: 'New',
    subLinks: [
      {
        label: 'Tutorial',
        i18nKey: 'menu.tutorial',
        icon: navIcon(GraduationCap),
        link: '/cukai/tutorial',
        isMinimized: false,
        badge: 'Mulai',
      },
      {
        label: 'Surat Jalan',
        i18nKey: 'menu.suratJalan',
        icon: navIcon(Truck),
        link: '/cukai/surat-jalan',
        isMinimized: false,
      },
      {
        label: 'Dokumen CK',
        i18nKey: 'menu.dokumenCk',
        icon: navIcon(Stamp),
        link: '/cukai/documents',
        isMinimized: false,
      },
      {
        label: 'Bill of Material',
        i18nKey: 'menu.billOfMaterial',
        icon: navIcon(Boxes),
        link: '/cukai/bom',
        isMinimized: false,
      },
      {
        label: 'Inventori',
        i18nKey: 'menu.inventori',
        icon: navIcon(Warehouse),
        link: '/cukai/inventory',
        isMinimized: false,
      },
      {
        label: 'Risk Self-Check',
        i18nKey: 'menu.riskSelfCheck',
        icon: navIcon(Shield),
        link: '/cukai/compliance',
        isMinimized: false,
        badge: 'AI Ready',
      },
    ],
  },
  {
    label: 'Customer Approval',
    i18nKey: 'menu.customerApproval',
    icon: navIcon(ClipboardCheck),
    link: '#',
    subLinks: [
      {
        label: 'Approval Requests',
        i18nKey: 'menu.approvalRequests',
        icon: navIcon(ClipboardCheck),
        link: '/customer-approval',
        isMinimized: false,
      },
      {
        label: 'Email Groups',
        i18nKey: 'menu.emailGroups',
        icon: navIcon(List),
        link: '/customer-approval/email-groups',
        isMinimized: false,
      },
      {
        label: 'Reviewers',
        i18nKey: 'menu.reviewers',
        icon: navIcon(UsersRound),
        link: '/customer-approval/reviewers',
        isMinimized: false,
      },
      {
        label: 'Roles & Permissions',
        i18nKey: 'menu.rolesPermissions',
        icon: navIcon(Shield),
        link: '/customer-approval/roles',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Credit Topup',
    i18nKey: 'menu.creditTopup',
    icon: navIcon(CreditCard),
    link: '/credit/topup',
  },
  {
    label: 'Langganan',
    i18nKey: 'menu.langganan',
    icon: navIcon(Crown),
    link: pageUrl.subscription,
  },
  {
    label: 'Berita',
    i18nKey: 'menu.berita',
    icon: navIcon(Newspaper),
    link: '#',
    subLinks: [
      {
        label: 'Antara News',
        i18nKey: 'menu.antaraNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=antara',
        isMinimized: false,
      },
      {
        label: 'CNN Indonesia',
        i18nKey: 'menu.cnnIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnn',
        isMinimized: false,
      },
      {
        label: 'CNBC Indonesia',
        i18nKey: 'menu.cnbcIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnbc',
        isMinimized: false,
      },
      {
        label: 'Republika News',
        i18nKey: 'menu.republikaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=republika',
        isMinimized: false,
      },
      {
        label: 'Kumparan News',
        i18nKey: 'menu.kumparanNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=kumparan',
        isMinimized: false,
      },
      {
        label: 'VOA News',
        i18nKey: 'menu.voaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=voa',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Layanan Lainnya',
    i18nKey: 'menu.layananLainnya',
    icon: navIcon(Settings),
    link: '#',
    subLinks: [
      {
        label: 'Stamp Duty',
        i18nKey: 'menu.stampDuty',
        icon: navIcon(FileInput),
        link: pageUrl.stampDuty,
        isMinimized: false,
      },
      {
        label: 'Mini Games',
        i18nKey: 'menu.miniGames',
        icon: navIcon(Gamepad2),
        link: '/mini-games',
        isMinimized: false,
        badge: 'New',
      },
      {
        label: 'File Compress',
        i18nKey: 'menu.fileCompress',
        icon: navIcon(FileArchive),
        link: pageUrl.fileCompression,
        isMinimized: false,
      },
      {
        label: 'Split PDF',
        i18nKey: 'menu.splitPdf',
        icon: navIcon(Scissors),
        link: '/split-pdf',
        isMinimized: false,
        badge: 'New',
      },
    ],
  },
  {
    label: 'Master',
    i18nKey: 'menu.master',
    icon: navIcon(Database),
    link: '#',
    subLinks: [
      {
        label: 'Ceisa User',
        i18nKey: 'menu.ceisaUser',
        icon: navIcon(UsersRound),
        link: '/master/ceisa-user',
        isMinimized: false,
      },
      {
        label: 'User',
        i18nKey: 'menu.user',
        icon: navIcon(UsersRound),
        link: '/master/user',
        isMinimized: false,
      },
      {
        label: 'Sea Carriers',
        i18nKey: 'menu.seaCarriers',
        icon: navIcon(Ship),
        link: '/master/sea-carriers',
        isMinimized: false,
      },
      {
        label: 'Export Bank',
        i18nKey: 'menu.exportBank',
        icon: navIcon(Landmark),
        link: '/master/export-bank',
        isMinimized: false,
      },
      {
        label: 'Email',
        i18nKey: 'menu.email',
        icon: navIcon(Mail),
        link: '/master/email',
        isMinimized: false,
      },
      {
        label: 'Customer Email Forwarding',
        i18nKey: 'menu.customerEmailForwarding',
        icon: navIcon(Forward),
        link: pageUrl.customerEmailForwarding,
        isMinimized: false,
      },
      {
        label: 'Statement',
        i18nKey: 'menu.statement',
        icon: navIcon(ClipboardCheck),
        link: '/master/statement',
        isMinimized: false,
      },
      {
        label: 'Data Consignee',
        i18nKey: 'menu.dataConsignee',
        icon: navIcon(Building2),
        link: '/master/consignee',
        isMinimized: false,
      },
      {
        label: 'Data Eksportir',
        i18nKey: 'menu.dataEksportir',
        icon: navIcon(Factory),
        link: '/master/eksportir',
        isMinimized: false,
      },
      {
        label: 'Master Barang',
        i18nKey: 'menu.masterBarang',
        icon: navIcon(Package),
        link: '/master/barang',
        isMinimized: false,
        badge: 'New',
        showSubBadge: true,
      },
      {
        label: 'Manifest Reminder',
        i18nKey: 'menu.manifestReminder',
        icon: navIcon(Bell),
        link: '/master/manifest-reminder',
        isMinimized: false,
      },
      {
        label: 'Revoke Access',
        i18nKey: 'menu.revokeAccess',
        icon: navIcon(Ban),
        link: '/master/revoke-access',
        isMinimized: false,
      },
    ],
  },
];

/**
 * Every navigable deep link the sidebar knows about, flattened.
 *
 * MenuAccessGuard checks a directly-typed URL against this catalog so route
 * access and sidebar visibility are decided from the same source: if a path
 * belongs to a menu entry the user's role did not grant, the page is blocked
 * rather than merely hidden from the nav.
 */
export const ALL_MENU_LINKS: string[] = menus
  .flatMap((item) => [
    item.link,
    ...((item as { subLinks?: { link?: string }[] }).subLinks ?? []).map((sub) => sub.link),
  ])
  .filter((link): link is string => typeof link === 'string' && link !== '' && link !== '#')
  .map((link) => link.split('?')[0]);

/* ------------------------------------------------------------------ *
 *  Shared pieces
 *
 *  The rail and the onboarding film (src/features/first-login) render the
 *  same identity, the same menu filtering, the same surface and the same
 *  header. They live here, next to `menus`, so the film cannot drift from
 *  what the user actually sees.
 * ------------------------------------------------------------------ */

/** Who is looking at the rail: email, impersonation and professional mode, kept in sync with storage. */
export const useSidebarIdentity = () => {
  const [userEmail, setUserEmail] = useState<string>('');
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [professionalModeEnabled, setProfessionalModeEnabled] = useState<boolean>(false);
  // /cms/me answers for the impersonator during an impersonation.
  const { canImpersonate } = useStaffAccess();

  const updateUserInfo = () => {
    const authData = localStorage.getItem('authData');
    setIsImpersonating(impersonationUtils.isImpersonating());

    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        setUserEmail(parsed.email || '');
      } catch (error) {
        console.error('Error parsing auth data:', error);
        setUserEmail('');
      }
    } else {
      setUserEmail('');
    }
  };

  useEffect(() => {
    updateUserInfo();

    // Listen for storage changes to update UI when impersonation state changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authData' || e.key === 'impersonationSession' || e.key === 'originalAuthData') {
        updateUserInfo();
      }
    };

    // Listen for custom events from impersonation actions
    const handleImpersonationChange = () => {
      updateUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  useEffect(() => {
    const updateProfessionalMode = () => {
      setProfessionalModeEnabled(isProfessionalModeEnabled());
    };

    updateProfessionalMode();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', updateProfessionalMode);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', updateProfessionalMode);
      }
    };
  }, []);

  return { userEmail, canImpersonate, isImpersonating, professionalModeEnabled };
};

/** The menu list exactly as the rail shows it for this user, plus the module lock lookup. */
export const useSidebarMenus = ({
  userEmail,
  professionalModeEnabled,
  searchQuery = '',
}: {
  userEmail: string;
  professionalModeEnabled: boolean;
  searchQuery?: string;
}) => {
  const { superAdmin } = useStaffAccess();

  const displayedMenus = useMemo(() => {
    const email = userEmail.toLowerCase();
    const isKepabeananViewOnly = KEPABEANAN_VIEW_ONLY_EMAILS.includes(email);
    const isManifestViewOnly = MANIFEST_VIEW_ONLY_EMAILS.includes(email);

    let result = menus;

    // Billing (Credit Topup + Langganan) is hidden by build flag, not by role:
    // the routes themselves redirect to /dashboard in App.tsx.
    if (!BILLING_ENABLED) {
      result = result.filter((item) => item.label !== 'Credit Topup' && item.label !== 'Langganan');
    }

    if (professionalModeEnabled) {
      result = result.map((item) => {
        if (item.label !== 'Layanan Lainnya' || !item.subLinks) {
          return item;
        }
        return {
          ...item,
          subLinks: item.subLinks.filter((subItem) => subItem.label !== 'Stamp Duty' && subItem.label !== 'Mini Games'),
        };
      });
    }

    // For non-super-admin users, filter Master subLinks to only show User and Email
    if (!superAdmin) {
      result = result.map((item) => {
        if (item.label !== 'Master' || !item.subLinks) return item;
        return {
          ...item,
          subLinks: item.subLinks.filter(
            (sub) =>
              sub.label === 'User' ||
              sub.label === 'Email' ||
              sub.label === 'Customer Email Forwarding' ||
              sub.label === 'Statement' ||
              sub.label === 'Data Consignee' ||
              sub.label === 'Data Eksportir' ||
              sub.label === 'Master Barang' ||
              sub.label === 'Manifest Reminder' ||
              sub.label === 'Export Bank'
          ),
        };
      });
    }

    // Hide "Buat Aju" for view-only users
    if (isKepabeananViewOnly || isManifestViewOnly) {
      result = result.map((item) => {
        if (!item.subLinks) return item;

        if (item.label === 'Kepabeanan' && isKepabeananViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        if (item.label === 'Manifest' && isManifestViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        return item;
      });
    }

    return result;
  }, [professionalModeEnabled, superAdmin, userEmail]);

  const roleFilteredMenus = useMemo(() => {
    return displayedMenus.filter((item) => {
      if (userEmail.toLowerCase().includes('samuel')) {
        return item.label === 'Dashboard';
      }
      if (userEmail.toLowerCase().includes('fedex.com') && item.label === 'Mgmt Dashboard') {
        return false;
      }
      if (isDemoSession()) {
        return item.label !== 'SSM' && item.label !== 'Stamp Duty';
      }
      if (userEmail.toLowerCase().includes('doc%')) {
        return item.label !== 'Kepabeanan';
      }
      if (item.label === 'Master') {
        if (superAdmin) {
          return true;
        }
        if (
          userEmail.toLowerCase().includes('nissinti') &&
          userEmail.toLowerCase() !== 'ibnu.wardhana@nissinti.co.id'
        ) {
          return false;
        }
        return true;
      }
      return true;
    });
  }, [displayedMenus, superAdmin, userEmail]);

  // Custom-role menu access. `canAccessMenu` fails open only for users still
  // on their legacy role, so the sidebar narrows as soon as an admin assigns a
  // custom role — including one that grants no menus at all.
  const { canAccessMenu } = usePermissions();

  const permissionFilteredMenus = useMemo(() => {
    return roleFilteredMenus
      .map((item) => {
        if (!item.subLinks) return item;
        const subLinks = item.subLinks.filter((sub) => canAccessMenu(sub.link));
        return { ...item, subLinks };
      })
      .filter((item) => {
        // A group survives if any of its children survived; a plain link is
        // checked directly.
        if (item.subLinks) return item.subLinks.length > 0;
        return canAccessMenu(item.link);
      });
  }, [roleFilteredMenus, canAccessMenu]);

  // Per-company module locks: locked groups stay visible but dimmed, and
  // clicking them opens the contact-admin dialog instead of navigating.
  const { isFeatureEnabled } = useCompanyFeatures();

  const filteredMenus = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return permissionFilteredMenus;

    return permissionFilteredMenus
      .map((item) => {
        const labelMatch = item.label.toLowerCase().includes(q);
        if (!item.subLinks) {
          return labelMatch ? item : null;
        }
        const matchedSubs = item.subLinks.filter((sub) => sub.label.toLowerCase().includes(q));
        if (labelMatch) return item;
        if (matchedSubs.length > 0) {
          return { ...item, subLinks: matchedSubs };
        }
        return null;
      })
      .filter(Boolean) as typeof permissionFilteredMenus;
  }, [permissionFilteredMenus, searchQuery]);

  return { filteredMenus, isFeatureEnabled };
};

/** Colours and the glass/solid surface the rail paints, derived from the active theme. */
export const useSidebarSurface = () => {
  const { mode, colors: themeColors } = useTheme();
  const isDarkSidebar = !LIGHT_SIDEBAR_MODES.includes(mode);
  // Same tint as dropdown and menu rows (ThemeContext --jarvis-hover).
  const hoverBg = 'var(--jarvis-hover)';
  const mutedText = isDarkSidebar ? 'rgba(255, 255, 255, 0.55)' : themeColors.textSecondary;
  const isJarvisBrand = mode === 'jarvis' || mode === 'jarvis-night';

  // A plain, solid rail in the theme's own sidebar colour, with one hairline edge.
  const sidebarStyle = {
    backgroundColor: themeColors.sidebar ?? themeColors.card,
    backgroundImage: 'none',
    borderRight: `1px solid ${isDarkSidebar ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 42, 68, 0.1)'}`,
    color: themeColors.text,
  };

  // Futurist accent + aurora palette (follows active theme)
  const accent = themeColors.accent ?? themeColors.primary ?? '#2A6B62';
  // JARVIS modes: brand-blue aurora instead of the horizon teal
  const aurora: [string, string] = isJarvisBrand
    ? isDarkSidebar
      ? ['rgba(37, 99, 235, 0.32)', 'rgba(37, 99, 235, 0.16)']
      : ['rgba(37, 99, 235, 0.16)', 'rgba(37, 99, 235, 0.08)']
    : isDarkSidebar
      ? ['rgba(91, 125, 212, 0.7)', 'rgba(42, 107, 98, 0.55)']
      : ['rgba(42, 107, 98, 0.5)', 'rgba(91, 125, 212, 0.42)'];

  return { isDarkSidebar, hoverBg, mutedText, isJarvisBrand, sidebarStyle, accent, aurora };
};

/** Logo strip at the top of the rail. `onClose` renders the mobile drawer's close button. */
export const SidebarBrandHeader = ({
  isMinimized,
  isGatewayUser,
  onClose,
}: {
  isMinimized: boolean;
  isGatewayUser: boolean;
  onClose?: () => void;
}) => {
  const { colors: themeColors } = useTheme();
  const { isDarkSidebar, mutedText } = useSidebarSurface();
  return (
    <div
      className={`flex-shrink-0 flex items-center relative overflow-hidden ${
        isMinimized ? 'justify-center px-2 h-12' : 'gap-2.5 px-3 h-12'
      }`}
      style={{ borderBottom: `1px solid ${themeColors.border}` }}
    >
      {isGatewayUser ? (
        <img
          className={`shrink-0 ${isGatewayUser ? 'h-7 w-auto max-w-[100px] object-contain' : 'h-7 w-7'} rounded-md`}
          src="/image/jpg/gatewaycontainerline_logo.jpeg"
          alt="Gateway"
        />
      ) : (
        /*
         * The wordmark sits straight on the header, no pill and no ring, at
         * full size. Same asset the login page uses: white on dark sidebars,
         * ink on light ones. Minimized, the wordmark gives way to the J mark.
         */
        <img
          className={`relative z-10 shrink-0 w-auto object-contain ${
            isMinimized ? 'h-6 max-w-[40px]' : 'h-[26px] max-w-[178px]'
          }`}
          src={`/image/png/jarvis-${isMinimized ? 'mark' : 'wordmark'}${isDarkSidebar ? '-white' : ''}.png`}
          alt="JARVIS"
        />
      )}
      {/* The email lives in the Kelola akun row now, so the wordmark gets the header. */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md max-[991px]:flex hidden"
          style={{ color: mutedText }}
          aria-label="Close navigation"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
};

export interface SidebarFooterProps {
  isMinimized: boolean;
  userEmail: string;
  canImpersonate: boolean;
  isImpersonating: boolean;
  onLogout?: () => void;
  isLoggingOut?: boolean;
  onToggleMinimize?: () => void;
  /** Runs before a footer link navigates (the rail closes its mobile drawer). */
  onNavigate?: () => void;
  /** Paints a row in its hover state; the onboarding film uses it to point at Pengaturan. */
  highlight?: 'pengaturan';
  /**
   * Emit the `data-tour` anchors (help pill, language). The onboarding film
   * renders a second copy of this footer and passes false, so a running
   * tour's querySelector can only ever match the live rail.
   */
  tourAnchors?: boolean;
  /** Live shell controls (notifications, trial badge, update reload); off in the onboarding preview. */
  withShellControls?: boolean;
}

/**
 * The rail's footer: account row, impersonation, theme, Pengaturan, changelog,
 * help, language, logout, collapse. Same JSX the rail always had, now shared
 * with the onboarding film so the film shows the real footer.
 */
export const SidebarFooter = ({
  isMinimized,
  userEmail,
  canImpersonate,
  isImpersonating,
  onLogout,
  isLoggingOut = false,
  onToggleMinimize,
  onNavigate,
  highlight,
  tourAnchors = true,
  withShellControls = true,
}: SidebarFooterProps) => {
  const navigate = useNavigate();
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { hoverBg, mutedText } = useSidebarSurface();
  // Shares the gate's query, so the badge and the blocking modal can never
  // disagree about what is unread.
  const unreadPatchLogs = useUnreadPatchLogCount();
  const hapticMedium = useHaptic('medium');
  const hapticFirm = useHaptic('firm');
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // Everything but Jadikan Pengguna sits in a pop-up on the profile row. It closes
  // on Esc, on navigation, and on a click outside it; clicks inside the theme and
  // language pickers (their own portalled dropdowns) keep it open.
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    setAccountOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (accountRef.current?.contains(target) || accountDropdownRef.current?.contains(target)) return;
      if (target.closest('.mantine-Menu-dropdown, .mantine-Popover-dropdown, .mantine-Combobox-dropdown')) return;
      setAccountOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen]);
  // Matches dropdown rows: 34px tall, 6px corners.
  const popupRowClass = 'w-full min-h-[34px] flex items-center gap-2.5 px-2.5 py-1.5 rounded-[6px] transition-colors duration-150 relative';

  const footerBtnClass = isMinimized
    ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150 relative'
    : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150 relative';

  const applyHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
  };
  const clearHover = (e: React.MouseEvent<HTMLElement>, bg = 'transparent') => {
    e.currentTarget.style.backgroundColor = bg;
  };

  return (
    <div
      className={`flex-shrink-0 space-y-0.5 ${isMinimized ? 'px-1.5 py-2' : 'px-2.5 py-2'}`}
      style={{ borderTop: `1px solid ${themeColors.border}` }}
    >
      <div ref={accountRef} {...(tourAnchors ? { 'data-tour': 'nav-language' } : {})}>
        <Popover
          opened={accountOpen}
          onChange={setAccountOpen}
          // Opens beside the sidebar, bottom-aligned with the profile row, so it never covers the menu.
          // The offset clears the profile row's inset and the sidebar edge.
          position="right-end"
          width={240}
          offset={20}
          closeOnClickOutside={false}
          closeOnEscape
          withinPortal
          shadow="md"
          radius="md"
        >
          {/* The target must be a plain element (no conditional or Tooltip around it). */}
          <Popover.Target>
            <button
              type="button"
              title={isMinimized ? userEmail : undefined}
              aria-label={userEmail || 'Akun'}
              onClick={() => setAccountOpen((open) => !open)}
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
              {...(tourAnchors ? { 'data-tour': 'tour-help-pill' } : {})}
              className={footerBtnClass}
              style={{ color: themeColors.text, backgroundColor: 'transparent' }}
              onMouseEnter={applyHover}
              onMouseLeave={(e) => clearHover(e)}
            >
              <div
                className={`rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${
                  isMinimized ? 'w-7 h-7 text-xs' : 'w-6 h-6 text-[11px]'
                }`}
                style={{ backgroundColor: themeColors.primary }}
              >
                {userEmail.charAt(0).toUpperCase()}
              </div>
              {/*
              Just the email. The "Kelola akun" label and its UserCog sat on
              a plain div with no onClick and no route behind it -- there is
              no account page in pageUrl -- so they promised an action that
              did not exist.
            */}
              {!isMinimized && (
                <span className="truncate flex-1 text-left text-[13px]" title={userEmail}>
                  {userEmail}
                </span>
              )}
              {/* Unread release notes stay visible while Riwayat Pembaruan is inside the pop-up. */}
              {unreadPatchLogs > 0 && (
                <span
                  aria-label={`${unreadPatchLogs} pembaruan belum dibaca`}
                  className={`shrink-0 rounded-full ${isMinimized ? 'absolute right-1.5 top-1.5' : ''}`}
                  style={{ width: 7, height: 7, backgroundColor: '#e03131' }}
                />
              )}
              {!isMinimized && (
                // Points to where the account menu opens: beside the sidebar.
                <ChevronRight size={15} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
              )}
            </button>
          </Popover.Target>
          <Popover.Dropdown
            ref={accountDropdownRef}
            className="jv-account-popover"
            p={6}
            style={{ backgroundColor: themeColors.card, border: `1px solid ${themeColors.border}` }}
          >
            <div className="space-y-0.5">
              {/* Personal settings. Out of `menus` for the same reason as the changelog
          below, and out of MODULES for one more: this is the page where a user
          turns things off, so narrowing a company's modules must never take it
          away. */}
              <Tooltip label={t('menu.pengaturan')} position="right" disabled withArrow>
                <button
                  type="button"
                  onClick={() => {
                    hapticMedium();
                    onNavigate?.();
                    navigate('/pengaturan');
                  }}
                  className={popupRowClass}
                  style={{
                    color: themeColors.text,
                    backgroundColor: highlight === 'pengaturan' ? hoverBg : 'transparent',
                  }}
                  onMouseEnter={applyHover}
                  onMouseLeave={(e) => clearHover(e, highlight === 'pengaturan' ? hoverBg : 'transparent')}
                  aria-label={t('menu.pengaturan')}
                  data-nav-footer="pengaturan"
                >
                  <Settings size={15} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
                  <span className="text-[13px] truncate flex-1 text-left">{t('menu.pengaturan')}</span>
                </button>
              </Tooltip>

              {/* Published changelog. Deliberately NOT a `menus` entry: ALL_MENU_LINKS is
          derived from that array and MenuAccessGuard blocks any path it owns for
          roles without the grant — release notes must reach every user. */}
              <Tooltip label={t('menu.riwayatPembaruan')} position="right" disabled withArrow>
                <button
                  type="button"
                  onClick={() => {
                    hapticMedium();
                    onNavigate?.();
                    navigate(pageUrl.riwayatPembaruan);
                  }}
                  className={popupRowClass}
                  style={{ color: themeColors.text, backgroundColor: 'transparent' }}
                  onMouseEnter={applyHover}
                  onMouseLeave={(e) => clearHover(e)}
                  aria-label={t('menu.riwayatPembaruan')}
                >
                  <History size={15} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
                  <span className="text-[13px] truncate flex-1 text-left">{t('menu.riwayatPembaruan')}</span>
                  {/* Unread updates (Issue 13). Survives "remind me later": the
              modal stops interrupting after three deferrals, but the
              release notes are still unread and the badge says so. */}
                  {unreadPatchLogs > 0 && (
                    <span
                      aria-label={`${unreadPatchLogs} pembaruan belum dibaca`}
                      className="shrink-0 rounded-full px-1.5 text-[10px] font-semibold leading-[16px] text-white"
                      style={{ backgroundColor: '#e03131' }}
                    >
                      {unreadPatchLogs > 9 ? '9+' : unreadPatchLogs}
                    </span>
                  )}
                </button>
              </Tooltip>

              {/* Always labelled: the pop-up has room even when the rail is collapsed. */}
              <TourHelpButton isMinimized={false} className={popupRowClass} withAnchor={false} />

              <LanguageSwitcher position="right" />

              <Tooltip label={t('sidebar.logout')} position="right" disabled withArrow>
                <button
                  type="button"
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  className={`${popupRowClass} ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ color: themeColors.error, backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    if (!isLoggingOut) e.currentTarget.style.backgroundColor = `${themeColors.error}10`;
                  }}
                  onMouseLeave={(e) => clearHover(e)}
                >
                  <Power size={15} strokeWidth={1.5} className="shrink-0" />
                  <span className="text-[13px] truncate flex-1 text-left">
                    {isLoggingOut ? t('sidebar.loggingOut') : t('sidebar.logout')}
                  </span>
                </button>
              </Tooltip>
            </div>
          </Popover.Dropdown>
        </Popover>
      </div>

      {withShellControls && (
        <NotificationBell variant="sidebar" isMinimized={isMinimized} rowClassName={footerBtnClass} />
      )}

      {canImpersonate && (
        <Menu position="right-start" offset={8} transitionProps={{ transition: 'fade', duration: 150 }}>
          <Menu.Target>
            <Tooltip label={t('sidebar.impersonate')} position="right" disabled={!isMinimized} withArrow>
              <button
                type="button"
                className={footerBtnClass}
                style={{
                  color: isImpersonating ? themeColors.warning : themeColors.text,
                  backgroundColor: isImpersonating ? `${themeColors.warning}15` : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isImpersonating) applyHover(e);
                }}
                onMouseLeave={(e) => {
                  if (!isImpersonating) clearHover(e);
                }}
              >
                <span className="text-[14px] shrink-0">🎭</span>
                {!isMinimized && (
                  <span className="text-[13px] truncate flex-1 text-left">
                    {isImpersonating ? t('sidebar.impersonating') : t('sidebar.impersonate')}
                  </span>
                )}
              </button>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown
            style={{
              backgroundColor: themeColors.card,
              border: `1px solid ${themeColors.border}`,
              borderRadius: 8,
              padding: 12,
              minWidth: 320,
              maxWidth: 400,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <ImpersonationSelect />
          </Menu.Dropdown>
        </Menu>
      )}

      {withShellControls && (
        <div className={`flex items-center gap-1.5 [&:empty]:hidden ${isMinimized ? 'flex-col py-1' : 'px-3 py-1'}`}>
          <TrialBadge />
          <AppUpdateHeaderButton />
        </div>
      )}

      {/* Collapse / expand — desktop only, always visible (not in the pop-up). */}
      <Tooltip
        label={
          isMinimized
            ? `${t('sidebar.expand')} ${isMac ? '⌘B' : 'Ctrl+B'}`
            : `${t('sidebar.collapse')} ${isMac ? '⌘B' : 'Ctrl+B'}`
        }
        position="right"
        withArrow
      >
        <button
          type="button"
          onClick={() => {
            hapticFirm();
            onToggleMinimize?.();
          }}
          className={`hidden min-[992px]:flex ${footerBtnClass}`}
          style={{ color: mutedText, backgroundColor: 'transparent' }}
          onMouseEnter={applyHover}
          onMouseLeave={(e) => clearHover(e)}
          aria-label={isMinimized ? t('sidebar.expand') : t('sidebar.collapse')}
        >
          {isMinimized ? (
            <ChevronsRight size={18} strokeWidth={1.5} />
          ) : (
            <>
              <ChevronsLeft size={16} strokeWidth={1.5} className="shrink-0" />
              <span className="text-[13px] truncate flex-1 text-left">{t('sidebar.collapse')}</span>
            </>
          )}
        </button>
      </Tooltip>
    </div>
  );
};

const SidebarV2 = ({ isOpen, isMinimized, toggleDrawer, toggleMinimize, isVisible = true }: SidebarV2Props) => {
  const { logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { userEmail, canImpersonate, isImpersonating, professionalModeEnabled } = useSidebarIdentity();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const hapticLight = useHaptic('light');
  const hapticMedium = useHaptic('medium');
  const hapticFirm = useHaptic('firm');

  const isGatewayUser = userEmail.toLowerCase().includes('gateway');
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const { isDarkSidebar, hoverBg, mutedText, sidebarStyle } = useSidebarSurface();

  const closeMobileDrawer = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT && isOpen) {
      toggleDrawer();
    }
  };

  // Prevent body scroll when sidebar is open on mobile and portrait tablet view
  useEffect(() => {
    if (isOpen && window.innerWidth < MOBILE_BREAKPOINT) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled, searchQuery });
  const [lockedModuleLabel, setLockedModuleLabel] = useState<string | null>(null);

  // ⌘K / Ctrl+K focuses quick search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (isMinimized) return;
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMinimized]);

  const handleLogout = () => {
    hapticFirm();
    logout(() => {
      notifications.show({ title: 'Success', message: 'Logout success!', color: 'green' });
      navigate('/auth');
    });
  };

  /** Light haptic buzz while the menu scrolls on touch devices. */
  const lastScrollBuzzRef = useRef(0);
  const handleNavScroll = () => {
    // On touch devices fire a throttled light buzz while scrolling (desktop has no motor).
    const now = Date.now();
    if (now - lastScrollBuzzRef.current > 140) {
      lastScrollBuzzRef.current = now;
      haptic(HAPTIC.light);
    }
  };

  const footerBtnClass = isMinimized
    ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150 relative'
    : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150 relative';

  const applyHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
  };
  const clearHover = (e: React.MouseEvent<HTMLElement>, bg = 'transparent') => {
    e.currentTarget.style.backgroundColor = bg;
  };

  return (
    <>
      {/* Mobile overlay drawer backdrop — only below AppShell md */}
      {isVisible && isOpen && (
        <div
          role="button"
          tabIndex={0}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 max-[991px]:block hidden"
          onClick={toggleDrawer}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleDrawer();
          }}
          aria-label="Close navigation overlay"
        />
      )}

      <div
        className={`h-full w-full flex flex-col z-50
          max-[991px]:fixed max-[991px]:inset-y-0 max-[991px]:left-0 max-[991px]:w-[min(17.5rem,85vw)]
          min-[992px]:relative
          ${isOpen ? 'translate-x-0' : 'max-[991px]:-translate-x-full min-[992px]:translate-x-0'}
          transition-transform duration-300 ease-in-out
        `}
        style={sidebarStyle}
      >
        <SidebarBrandHeader
          isMinimized={isMinimized}
          isGatewayUser={isGatewayUser}
          onClose={() => {
            hapticLight();
            toggleDrawer();
          }}
        />

        {/* Search + nav */}
        <div
          ref={navScrollRef}
          onScroll={handleNavScroll}
          className={`relative flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent ${
            isMinimized ? 'px-1.5 py-2' : 'px-2.5 py-2'
          }`}
        >
          {!isMinimized ? (
            <div
              data-tour="nav-search"
              className="flex items-center gap-2 px-2.5 py-1.5 mb-2 rounded-lg border"
              style={{
                borderColor: themeColors.border,
                backgroundColor: isDarkSidebar ? 'rgba(255,255,255,0.04)' : themeColors.card,
              }}
            >
              <Search size={14} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('sidebar.quickSearch')}
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-[13px]"
                style={{ color: themeColors.text }}
              />
              <kbd className="shrink-0 text-[10px] font-medium px-1" style={{ color: mutedText }}>
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </div>
          ) : (
            <Tooltip label="Quick search (expand sidebar)" position="right" withArrow>
              <button
                type="button"
                onClick={() => {
                  hapticMedium();
                  toggleMinimize();
                }}
                className={footerBtnClass}
                style={{ color: mutedText, backgroundColor: 'transparent' }}
                onMouseEnter={applyHover}
                onMouseLeave={(e) => clearHover(e)}
                aria-label="Expand to search"
                data-tour="nav-search"
              >
                <Search size={16} strokeWidth={1.5} />
              </button>
            </Tooltip>
          )}

          <div className="space-y-0.5" data-tour="nav-list">
            {filteredMenus.map((item) => {
              const moduleCode = MODULE_BY_SIDEBAR_LABEL[item.label];
              const locked = moduleCode ? !isFeatureEnabled(moduleCode) : false;
              return (
                // Wrapper carries the onboarding anchor: LinksGroup does not
                // spread unknown props, so data-tour cannot go on it directly.
                <div key={item.label} data-tour={sidebarAnchor(item.label)}>
                  <LinksGroup
                    {...item}
                    isMinimized={isMinimized}
                    onNavigate={closeMobileDrawer}
                    locked={locked}
                    onLockedClick={() => setLockedModuleLabel(item.label)}
                  />
                </div>
              );
            })}
            <FeatureLockedModal
              opened={lockedModuleLabel !== null}
              onClose={() => setLockedModuleLabel(null)}
              moduleLabel={lockedModuleLabel ?? undefined}
            />
            {filteredMenus.length === 0 && !isMinimized && (
              <p className="px-3 py-4 text-[12px] text-center" style={{ color: mutedText }}>
                {t('sidebar.noMatches')}
              </p>
            )}
          </div>
        </div>

        <SidebarFooter
          isMinimized={isMinimized}
          userEmail={userEmail}
          canImpersonate={canImpersonate}
          isImpersonating={isImpersonating}
          onLogout={handleLogout}
          isLoggingOut={isLoading}
          onToggleMinimize={toggleMinimize}
          onNavigate={closeMobileDrawer}
        />
      </div>
    </>
  );
};

export default SidebarV2;
